import { useState, useCallback } from 'react';
import { ethers } from 'ethers';
import { useWeb3 } from '../context/Web3Context';
import { useContracts } from './useContract';
import { useToast } from '../context/ToastContext';
import { parseError } from '../utils/errors';
import { toUSDT } from '../utils/format';

// Handles: ensure-connected, ensure USDT allowance, send tx, wait, notify.
export function useTx() {
  const { signer, correctChain, ensureNetwork, isConnected, connect } = useWeb3();
  const { evittaWrite, usdtWrite } = useContracts();
  const toast = useToast();

  const [pending, setPending] = useState(false);
  const [stage, setStage] = useState(''); // '', 'approve', 'confirm', 'mining'

  // Ensure the spender (Evitta) has at least `amountUSDT` allowance.
  const ensureAllowance = useCallback(
    async (amountUSDT, owner) => {
      if (!usdtWrite || !evittaWrite) throw new Error('Not ready.');
      const needed = toUSDT(amountUSDT);
      const current = await usdtWrite.allowance(owner, evittaWrite.address);
      if (current.gte(needed)) return;
      setStage('approve');
      toast.info('Approve USDT in your wallet…');
      // Approve a generous amount so future actions skip re-approving.
      const approveAmt = ethers.constants.MaxUint256;
      const tx = await usdtWrite.approve(evittaWrite.address, approveAmt);
      await tx.wait();
      toast.success('USDT approved.');
    },
    [usdtWrite, evittaWrite, toast]
  );

  // run({ method, args, value, needsApproval, successMsg, onDone })
  const run = useCallback(
    async (opts) => {
      const {
        method,
        args = [],
        approveAmount = 0,
        successMsg = 'Transaction confirmed!',
        onDone,
      } = opts;

      if (!isConnected) {
        await connect();
        return;
      }
      if (!correctChain) {
        await ensureNetwork();
        return;
      }
      if (!signer || !evittaWrite) {
        toast.error('Wallet not ready. Reconnect and try again.');
        return;
      }

      setPending(true);
      try {
        const owner = await signer.getAddress();

        if (approveAmount && Number(approveAmount) > 0) {
          await ensureAllowance(approveAmount, owner);
        }

        setStage('confirm');
        toast.info('Confirm the transaction in your wallet…');
        const tx = await evittaWrite[method](...args);

        setStage('mining');
        await tx.wait();

        toast.success(successMsg);
        if (onDone) await onDone(tx);
      } catch (err) {
        toast.error(parseError(err));
      } finally {
        setPending(false);
        setStage('');
      }
    },
    [isConnected, correctChain, signer, evittaWrite, connect, ensureNetwork, ensureAllowance, toast]
  );

  return { run, pending, stage };
}
